import {
    ShareBaseFolder,
    ShareBaseDocument
} from 'phoenix/domain';
import SettingsService from 'phoenix/services/settingsService';
import { AppAction } from 'sharebase-app/components/_app/app.actions';
import {
    ActionService,
    AuthService,
    EnvironmentService
} from 'phoenix/services';

export enum DesktopError {
    PORT_SCAN_FAILED = 'PORT_SCAN_FAILED', 
    EDIT_CONFLICT = 'EDIT_CONFLICT', 
    NO_EDITOR = 'NO_EDITOR', 
    USER_MISMATCH = 'USER_MISMATCH',
    NOT_AUTHENTICATED = 'NOT_AUTHENTICATED',
    NO_SCANNER = 'NO_SCANNER'
}

export class DesktopException extends Error {
    public type:string = '';
    constructor(public errorCode:DesktopError){
        super();
        this.type = DesktopError[errorCode];
        this.stack = (<any>new Error()).stack;
        this.name = 'DesktopException';
    }
}

export interface IDesktopService {
   port: number;
   desktopAvailable: boolean;
   polling: boolean;
   discover(): ng.IPromise<number>;
   getNotifications():ng.IPromise<any>;
   startPolling(): IDesktopService;
   subscribe(callback:NotificationsCallback): () => void;
   stopPolling():void;
   edit(document:ShareBaseDocument): ng.IPromise<void>;
   forgetDesktop(): IDesktopService;
   scan(folder: ShareBaseFolder): ng.IPromise<any>;
   getDownloadUrl(): ng.IPromise<any>;
   createErrorMessage(e:DesktopException):string;
};

interface DesktopCanceler {
    cancel():void;
}

export interface DesktopNotification {
    Command:string;
    TimeStamp:string;
    Embedded: {
        DocumentId:number;
        FolderId:number;
    }
}

interface DesktopInfo {
    AppId:number,
    Name:string,
    Version:string,
    Links: {
        InfoUri:string;
        UpdateUri:string;
    }
}

interface NotificationsCallback {
    (notifications:DesktopNotification[]): void
}

interface SubscriptionCollection{
    [key:string]:NotificationsCallback;
}

export const DesktopServices = {
    scan: 'imaging/acquireimage',
    edit: 'edit',
    discover: 'discover',
    notifications: 'users/{userId}/notifications'
};

const httpConfig = { 'skipAuth': true } as ng.IRequestShortcutConfig;
let pollInterval = 3000;
let numFails = 0;
let portRange = { 
    start: 9901, 
    end: 9915 
};
let maxFails = 1;
let pollingCanceler:DesktopCanceler;
let subscriptions:SubscriptionCollection = {};
let discoveryPromise: ng.IPromise<number>;

export class DesktopService implements IDesktopService {
    static $inject = [
        '$q', 
        '$http', 
        '$interval', 
        'debugInfoEnabled',
        'settingsService', 
        'pxActionService',
        'pxAuthService',
        'gettextCatalog'
    ];

    public port = 0;
    public desktopAvailable = false;
    public polling = false;

    public static create(
        pxEnvironmentService:EnvironmentService,
        $q:ng.IQService,
        $http:ng.IHttpService,
        $interval:ng.IIntervalService,
        debugInfoEnabled:boolean,
        settingsService:SettingsService,
        pxActionService:ActionService,
        pxAuthService:AuthService,
        gettextCatalog:ng.gettext.gettextCatalog
    ): IDesktopService {
        if (pxEnvironmentService.isWindows && !pxAuthService.isShareByLinkUser) {
            return new DesktopService($q, $http, $interval, debugInfoEnabled, settingsService, pxActionService, pxAuthService, gettextCatalog)
        } else {
            return new NullDesktopService($q);
        }
    }

    constructor(
        private $q: ng.IQService, 
        private $http: ng.IHttpService, 
        private $interval: ng.IIntervalService, 
        private debugInfoEnabled: boolean,
        private settingsService:SettingsService, 
        private pxActionService:ActionService,
        private pxAuthService:AuthService,
        private gettextCatalog:ng.gettext.gettextCatalog
    ) {
        if (angular.isDefined(settingsService.desktopPorts)) {
            let ports = settingsService.desktopPorts.split('-');

            if(ports.length > 0){
                portRange.start = +ports[0];
                portRange.end = ports.length > 1 ? +ports[1] : portRange.start;
            }            
        }

        if (angular.isDefined(settingsService.desktopPollInterval)) {
            pollInterval = +settingsService.desktopPollInterval;
        }

        maxFails = (portRange.end - portRange.start) + 1;
    }

    public forgetDesktop(){
        discoveryPromise = null;
        this.desktopAvailable = false;
        return this;
    }

    public createErrorMessage(e:DesktopException):string {
        let gettextCatalog = this.gettextCatalog;
        let msg = gettextCatalog.getString('Unable to edit this document.');
        if(e){
            switch (DesktopError[e.type]) {
                case DesktopError.PORT_SCAN_FAILED:
                    return gettextCatalog.getString('Unable to connect to the desktop.');
                case DesktopError.USER_MISMATCH:
                    return gettextCatalog.getString('A different user is logged into the desktop.');
                case DesktopError.NO_EDITOR:
                    return gettextCatalog.getString('The desktop could not find an application to open this file.');
                case DesktopError.NOT_AUTHENTICATED:
                    return gettextCatalog.getString('You must be logged into the desktop to edit documents.');
                case DesktopError.EDIT_CONFLICT:
                    return gettextCatalog.getString('This document is already being edited.');
            }
        }

        return msg;
    }

    public discover() {
        // if we already attempted discovery, simply return the promise from that
        if(discoveryPromise){
            return discoveryPromise;
        }

        this.polling = true;
        return discoveryPromise = this.pingPorts(portRange.start)
            .then(data =>{
                if(this.debugInfoEnabled){
                    console.log('Desktop service found at:', data.port);
                }               

                this.desktopAvailable = true;
                return this.port = data.port;
            })
            .catch(e => {
                if(this.debugInfoEnabled){
                    console.error('Desktop service not found.');
                }
                this.desktopAvailable = false;
                return this.$q.reject(e);
            })
            .finally(() => {
                numFails = 0;
                this.polling = false;
            });    
    }

    public startPolling(){
        let pollingPromise;
        if(pollInterval > 0 && !pollingCanceler) {
            pollingCanceler = {
                cancel: cancel.bind(this)
            };
            
            pollingPromise = this.$interval(() => {
                this.getNotifications()
                    .then(notifications => {
                        if(notifications.length > 0) {
                            Object.keys(subscriptions).forEach(k => {
                                subscriptions[k](notifications);
                            });
                        }
                    })
                    .catch(pollingCanceler.cancel);
            }, pollInterval);
        }
    
        function cancel(){
            this.$interval.cancel(pollingPromise);
            pollingCanceler = null;
        }

        return this;
    }

    public subscribe(callback:NotificationsCallback) {
        if(!angular.isFunction(callback)){
            throw new Error('A valid callback must be supplied.');
        }
        let key = Date.now().toString();

        subscriptions[key] = callback;

        return () => {
            subscriptions[key] = null;
            delete subscriptions[key];
            key = null;
        };
    }

    public stopPolling() {
        if(pollingCanceler){
            pollingCanceler.cancel();
        }
    }

    public getNotifications():ng.IPromise<DesktopNotification[]> {
        let resource = DesktopServices
            .notifications
            .replace('{userId}', this.pxAuthService.userId.toString());
        if(!this.pxAuthService.isAuthenticated) {
            return this.$q.reject(new Error('Must be authenticated to retrieve desktop notifcations.'));
        } 

        return this.discover()
            .then(() => {
                let uri = this.getDesktopUri(resource);
                return this.$http.get<DesktopNotification[]>(uri)
                    // Callbacks here are nested so the catch below
                    // doesn't run for `discover` calls and clears
                    // the promise.
                    .then(resp => resp.data)
                    .catch(this.resetDesktopStatus.bind(this));
            });
    }

    public edit(document){
        let file = document.Embedded.LatestFile;
        // shape transformation for desktop API
        let requestData = {
            "DocumentId": document.DocumentId,
            "UserId": this.pxAuthService.userId,
            "FileId": file.FileId,
            "FileSize": file.FileSize,
            "DocumentName": document.DocumentName,
            "OriginalFileName": file.OriginalFileName,
            "FileName": file.FileName,
            "MimeType": file.MimeType,
            "Links": {
                "Self": file.Links.Self,
                "Content": file.Links.Content.split('?')[0]// strip querystring
            }
        };

        return this.discover()
            .then(() =>{
                let uri = this.getDesktopUri(DesktopServices.edit);
                return this.$http.post(uri, requestData, httpConfig);
            })
            .catch(response => {
                let error;
                if (response === "PORT_SCAN_FAILED" || response.type === "PORT_SCAN_FAILED") {
                    return this.pxActionService.emit({type: AppAction.PromptForDesktopInstall});
                }

                let data = response.data;

                if (response.status === 403 && data.ErrorCode === "NO_EDITOR") {
                    error = new DesktopException(DesktopError.NO_EDITOR);
                } else if (response.status === 403 && data.ErrorCode === "MISMATCHED_USER") {
                    error = new DesktopException(DesktopError.USER_MISMATCH);
                } else if (response.status === 401 && data.ErrorCode === "NO_CLIENT_CREDENTIALS") {
                    error = new DesktopException(DesktopError.NOT_AUTHENTICATED);
                } else if (response.status === 409 && data.ErrorCode === "FILE_ALREADY_IN_EDIT") {
                    error = new DesktopException(DesktopError.EDIT_CONFLICT);
                } else {
                    error = data;
                }

                return this.$q.reject(error);
            });
    }

    public scan(folder: ShareBaseFolder) {
        let requestData = {
            FolderId: folder.FolderId,
            Upload: folder.Links.DocumentsUri,
            UserId: this.pxAuthService.userId
        };

        return this.discover()
            .then(() => {
                let uri = this.getDesktopUri(DesktopServices.scan);
                return this.$http.post(uri, requestData, httpConfig);
            })
            .catch((response: { status: number, data: { ErrorCode: string; }; }) => {
                let { status, data } = response;

                let error: any = response;
                if (status === 403 && data.ErrorCode === 'MISMATCHED_USER') {
                    error = new DesktopException(DesktopError.USER_MISMATCH);
                } else if (status === 401 && data.ErrorCode === 'NO_CLIENT_CREDENTIALS') {
                    error = new DesktopException(DesktopError.NOT_AUTHENTICATED);
                } else if (status === 403 && data.ErrorCode === 'NO_SCANNER') {
                    error = new DesktopException(DesktopError.NO_SCANNER);
                }

                return this.$q.reject(error);
            });
    }

    public getDownloadUrl() {
        return this.$http
            .get<any>(this.settingsService.serverAPIPath + '/update/apps/2')
            .then(response => response.data.Links.UpdateUri)
            .catch(() => {
                if(this.debugInfoEnabled){
                    console.error('Could not get Desktop download link.');
                }
            });
    }

    private pingPorts(startPort:number){
        let uri = this.getDesktopUri(DesktopServices.discover, startPort);
        let p:ng.IPromise<{port:number, desktopInfo:DesktopInfo}> = this.$http.get<DesktopInfo>(uri, httpConfig)
            .then(response => {
                return {
                    port: startPort,
                    desktopInfo: response.data
                };
            })
            .catch(e => {
                numFails++;
                if(numFails >= maxFails){
                    return this.$q
                        .reject(new DesktopException(DesktopError.PORT_SCAN_FAILED));
                } else {
                    return this.pingPorts(++startPort);
                }
            });     

        return p;
    }

    private getDesktopUri(resource:string, port:number = this.port){
        return `${this.settingsService.desktopPath}:${port}/${resource}`;
    }

    private resetDesktopStatus(e){
        this.forgetDesktop();
        return this.$q.reject(e);
    }
}

DesktopService.create.$inject = [
    'pxEnvironmentService',
    '$q',
    '$http',
    '$interval',
    'debugInfoEnabled',
    'settingsService',
    'pxActionService',
    'pxAuthService',
    'gettextCatalog'
];

/**
 * No-op service that will be used in environments that do not support the ShareBase desktop app.
 */
class NullDesktopService implements IDesktopService {
    public static $inject = [
        '$q'
    ];

    public readonly port = -1;
    public readonly desktopAvailable = false;
    public readonly polling = false;

    constructor(private $q:ng.IQService) { }

    public discover():ng.IPromise<number> {
        return this.$q.resolve(-1);
    }

    public getNotifications():ng.IPromise<any> {
        return this.$q.resolve();
    }

    public startPolling() {
        return this;
    }

    public subscribe(callback:NotificationsCallback) {
        return angular.noop;
    }

    public stopPolling():void { }

    public edit(document:any):ng.IPromise<any> {
        return this.$q.resolve();
    }

    public forgetDesktop() {
        return this;
    }

    public scan(folder: ShareBaseFolder): ng.IPromise<any> {
        return this.$q.resolve();
    }

    public getDownloadUrl():ng.IPromise<any>{        
        return this.$q.resolve();
    }

    public createErrorMessage() {
        return '';
    }
}


// WEBPACK FOOTER //
// ./src/sharebase/services/desktopService.ts